import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
  computed,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  DropdownComponent,
  DropdownItem,
  InfiniteScrollListDirective,
  InfiniteScrollListItemDirective,
  InputComponent,
  TooltipModule,
} from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, startWith } from 'rxjs';
import { DataState, Loadable } from '@libs/utils';
import { ActivatedRoute } from '@angular/router';
import { CollaborationListItemComponent } from '../collaboration-list-item';
import { CollaborationDynamicTitlePipe } from '../../pipes/collaboration-dynamic-title';
import { CollabSidebarElement } from './collab-sidebar.vm';
import { CollabSidebarService } from './collab-sidebar.service';

@Component({
  selector: 'lib-collab-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    DropdownComponent,
    TranslateModule,
    SvgIconComponent,
    ReactiveFormsModule,
    CollaborationListItemComponent,
    InfiniteScrollListDirective,
    InfiniteScrollListItemDirective,
    NgClass,
    CollaborationDynamicTitlePipe,
    TooltipModule,
  ],
  templateUrl: './collab-sidebar.component.html',
  styleUrl: './collab-sidebar.component.scss',
})
export class CollabSidebarComponent implements OnInit {
  public filterService = inject(CollabSidebarService);
  private readonly activatedRoute = inject(ActivatedRoute);

  @Input() public collabList: Loadable<CollabSidebarElement[]> = {
    data: [],
    state: DataState.Initial,
  };
  @Input() public currentUserIds: string[] = [];

  @Input() public set searchTerm(searchTerm: string | null | undefined) {
    this.searchTermControl.setValue(searchTerm ?? null, { emitEvent: false });
  }

  @Input() public sites: DropdownItem<string>[] = [];
  @Input() public patientDataState: DataState = DataState.Fetched;
  @Input() public set showSiteFilter(showSiteFilter: boolean) {
    this._showSiteFilter.set(showSiteFilter);
    if (showSiteFilter) {
      this.formGroup.controls.selectedSiteIds.enable();
    } else {
      this.formGroup.controls.selectedSiteIds.disable();
    }
  }
  @Input() public isDisabled: boolean = false;
  @Input() public isPatientContext: boolean = false;

  public get showSiteFilter(): boolean {
    return this._showSiteFilter();
  }

  @Input() public set selectedSiteIds(siteIds: string[] | null) {
    this.formGroup.patchValue(
      {
        selectedSiteIds: siteIds ?? [],
      },
      { emitEvent: false },
    );
  }

  @Input() public set unreadOnly(unreadOnly: boolean) {
    this.formGroup.patchValue({ unreadOnly }, { emitEvent: false });
  }
  @Input() public selectedCollab: Loadable<CollabSidebarElement> = {
    data: undefined,
    state: DataState.Empty,
  };
  @Input() public filtersOpen: boolean = false;
  public hasDeletedCollabs = input<boolean>(false);
  @Output() public createNewCollab = new EventEmitter<boolean>();
  @Output() public selectCollab = new EventEmitter<string>();
  @Output() public toggleFilters = new EventEmitter<void>();
  @Output() public clearFilters = new EventEmitter<void>();
  @Output() public collabListEndReached = new EventEmitter<void>();
  private readonly _showSiteFilter = signal(true);
  private readonly destroyRef = inject(DestroyRef);

  private readonly SEARCH_DEBOUNCE_TIME = 300;
  public DataState = DataState;
  protected readonly loadingSkeletons = computed(() => Array.from({ length: 4 }).fill(0));

  protected readonly searchTermControl = new FormControl<string | undefined>(undefined, { updateOn: 'change' });
  protected readonly formGroup = new FormGroup({
    selectedSiteIds: new FormControl<string[] | undefined>([]),
    directOnly: new FormControl<boolean>(false),
    unreadOnly: new FormControl<boolean>(false),
    teamCollab: new FormControl<boolean>(false),
    clinicalCollab: new FormControl<boolean>(false),
    recentlyDeleted: new FormControl<boolean>(false),
  });

  protected searchTermSignal = toSignal(this.searchTermControl.valueChanges.pipe(startWith(this.searchTermControl.value)));

  protected readonly formChangesSignal = toSignal(this.formGroup.valueChanges);
  protected readonly searchTermChangesSignal = toSignal(this.searchTermControl.valueChanges);
  protected readonly areFiltersEnabled = computed(
    () =>
      !!(
        this.searchTermChangesSignal()?.length ||
        this.formChangesSignal()?.selectedSiteIds?.length ||
        this.formChangesSignal()?.unreadOnly ||
        this.formChangesSignal()?.directOnly ||
        this.formChangesSignal()?.teamCollab ||
        this.formChangesSignal()?.clinicalCollab ||
        this.formChangesSignal()?.recentlyDeleted
      ),
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public ngOnInit(): void {
    this.searchTermControl.setValue(this.filterService.state().searchTerm ?? '', { emitEvent: false });
    this.formGroup.patchValue({
      selectedSiteIds: this.filterService.state().filterSiteIds ?? [],
      directOnly: this.filterService.state().filterDirectOnly ?? false,
      unreadOnly: this.filterService.state().filterUnreadOnly ?? false,
      teamCollab: this.filterService.state().filterTeamCollab ?? false,
      clinicalCollab: this.filterService.state().filterClinicalCollab ?? false,
      recentlyDeleted: this.filterService.state().filterRecentlyDeleted ?? false,
    });

    // Subscribe to search term changes with debounce
    this.searchTermControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(this.SEARCH_DEBOUNCE_TIME))
      .subscribe((searchTerm) => {
        this.filterService.updateFilters({
          searchTerm: searchTerm ?? '',
          selectedSiteIds: this.formGroup.controls.selectedSiteIds.value ?? [],
          unreadOnly: this.formGroup.controls.unreadOnly.value ?? false,
          directOnly: this.formGroup.controls.directOnly.value ?? false,
          teamCollab: this.formGroup.controls.teamCollab.value ?? false,
          clinicalCollab: this.formGroup.controls.clinicalCollab.value ?? false,
          recentlyDeleted: this.formGroup.controls.recentlyDeleted.value ?? false,
        });
      });

    // Subscribe to other filter changes without debounce
    this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ selectedSiteIds, unreadOnly, directOnly, teamCollab, clinicalCollab, recentlyDeleted }) => {
        this.filterService.updateFilters({
          searchTerm: this.searchTermControl.value ?? '',
          selectedSiteIds: (selectedSiteIds as string[]) ?? [],
          unreadOnly: unreadOnly as boolean,
          directOnly: directOnly as boolean,
          teamCollab: teamCollab as boolean,
          clinicalCollab: clinicalCollab as boolean,
          recentlyDeleted: recentlyDeleted as boolean,
        });
      });

    const unread = this.activatedRoute.snapshot.queryParams?.['unread'];

    if (unread) {
      this.formGroup.controls.unreadOnly.setValue(true);
    }
  }

  public onCreateNewCollab(): void {
    this.createNewCollab.emit(this.showSiteFilter);
  }

  public onListItemClick(collabId: string): void {
    this.selectCollab.emit(collabId);
  }

  public onClearFilters(): void {
    this.searchTermControl.setValue(null, { emitEvent: false });
    this.formGroup.patchValue({
      selectedSiteIds: [],
      directOnly: false,
      unreadOnly: false,
      teamCollab: false,
      clinicalCollab: false,
      recentlyDeleted: false,
    });
  }

  protected onCollabListEndReached(): void {
    this.collabListEndReached.emit();
  }

  protected clearSearchInput(): void {
    this.searchTermControl.setValue(null);
  }

  protected onDirectToMeClick(): void {
    this.formGroup.controls.directOnly.setValue(!this.formGroup.controls.directOnly.value);
  }

  protected onUnreadClick(): void {
    this.formGroup.controls.unreadOnly.setValue(!this.formGroup.controls.unreadOnly.value);
  }

  protected onTeamCollabClick(): void {
    this.formGroup.controls.teamCollab.setValue(!this.formGroup.controls.teamCollab.value);
  }

  protected onClinicalCollabClick(): void {
    this.formGroup.controls.clinicalCollab.setValue(!this.formGroup.controls.clinicalCollab.value);
  }

  protected onRecentlyDeletedClick(): void {
    this.formGroup.controls.recentlyDeleted.setValue(!this.formGroup.controls.recentlyDeleted.value);
  }

  protected onLeaveCollaborationClick(collabId: string): void {
    this.filterService.leaveCollaboration(collabId);
  }

  protected onRecoverCollaborationClick(collabId: string): void {
    this.filterService.recoverCollaboration(collabId);

    if (this.collabList?.data?.length === 1) {
      this.formGroup.controls.recentlyDeleted.setValue(false);
    }
  }

  protected onDeleteCollaborationClick(collabId: string, isPermanent: boolean): void {
    this.filterService.deleteCollaboration(collabId, isPermanent);

    if (isPermanent && this.collabList?.data?.length === 1) {
      this.formGroup.controls.recentlyDeleted.setValue(false);
    }
  }
}
